import {ChangeDetectorRef, Component, forwardRef, Inject, Input, NgZone, OnDestroy, PLATFORM_ID} from '@angular/core';
import {DashboardBaseComponent} from "../shared/dashboard-base-component";
import {DashboardBaseChart} from "../shared/dashboard-base-chart";
import {isPlatformBrowser} from "@angular/common";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5Local from "@amcharts/amcharts5/locales/es_ES";
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";

export interface ChartColumnSeries {
  field: string;
  name: string;
  color: string;
  colorField?: string;             // data field that overrides fill per bar
  showValueLabel?: boolean;        // show label above each bar
  valueLabelSuffix?: string;       // appended to label text e.g. '%'
  valueLabelFormatter?: (v: number) => string;  // custom label format e.g. convertToMillions
}

export interface ChartColumnConfig {
  chartId: string;
  categoryField: string;
  bars: ChartColumnSeries[];
  lines?: ChartColumnSeries[];  // optional
  showLegend?: boolean;          // default true
  yMin?: number;                 // optional Y axis floor
  yMax?: number;                 // optional Y axis ceiling
  yLabelFormatter?: (value: number) => string;
  tooltipValueFormatter?: (value: number) => string;
  xLabelRotation?: number;       // e.g. -45 for angled labels
  data: any[];
}

/**Esto es para armar un card, no hace ningun efecto en el componente */
export interface ChartColumnCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartColumnConfig;
}

@Component({
  selector: 'app-chart-column',
  templateUrl: './chart-column.component.html',
  styleUrls: ['./chart-column.component.scss'],
  standalone: true,
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartColumnComponent)
    }
  ]
})
export class ChartColumnComponent  extends DashboardBaseChart implements OnDestroy {

  @Input() config!: ChartColumnConfig;

  isLoading = true;

  private xAxis?: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private barSeriesList: am5xy.ColumnSeries[] = [];
  private lineSeriesList: am5xy.LineSeries[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      root.locale = am5Local;
      this.root = root;

      const textColor  = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const gridColor  = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingLeft: 0,
        })
      );

      // X axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 25 }),
        })
      );
      this.xAxis = xAxis;
      const xRotation = this.config.xLabelRotation;
      xAxis.get('renderer').labels.template.setAll({
        fill: mutedColor,
        fontSize: 11,
        ...(xRotation !== undefined ? {
          rotation: xRotation,
          centerX: am5.percent(100),
          centerY: am5.percent(50),
        } : {}),
      });
      xAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });

      // Y axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
          extraMax: 0.1,
          ...(this.config.yMin !== undefined ? { min: this.config.yMin } : {}),
          ...(this.config.yMax !== undefined ? { max: this.config.yMax } : {}),
          ...(this.config.yMin !== undefined || this.config.yMax !== undefined ? { strictMinMax: true } : {}),
        })
      );
      yAxis.get('renderer').labels.template.setAll({ fill: mutedColor, fontSize: 11 });
      yAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      if (this.config.yLabelFormatter) {
        const fmt = this.config.yLabelFormatter;
        yAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('value') as number | undefined;
          return value != null ? fmt(value) : _text;
        });
      }

      // Bar series
      for (const bar of this.config.bars) {
        const barColor = am5.color(bar.color);
        const hasPerBarColor = !!bar.colorField;

        const tooltip = am5.Tooltip.new(root, {
          labelText: `[bold #fff]{categoryX}: {valueY}${bar.valueLabelSuffix ?? ''}`,
        });
        const tooltipFmt = bar.valueLabelFormatter ?? this.config.tooltipValueFormatter;
        if (tooltipFmt) {
          tooltip.label.adapters.add('text', (_text, target) => {
            const value = (target.dataItem as any)?.get('valueY') as number | undefined;
            const cat = (target.dataItem as any)?.get('categoryX') as string | undefined;
            return value != null ? `[bold #fff]${cat ?? ''}: ${tooltipFmt(value)}` : _text;
          });
        }

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: bar.name,
            xAxis,
            yAxis,
            categoryXField: this.config.categoryField,
            valueYField: bar.field,
            sequencedInterpolation: true,
            tooltip,
          })
        );

        series.columns.template.setAll({
          fill: barColor,
          strokeOpacity: 0,
          width: am5.percent(80),
          cornerRadiusTL: 5,
          cornerRadiusTR: 5,
        });

        if (hasPerBarColor) {
          series.columns.template.adapters.add('fill', (_fill: any, target: any) => {
            const ctx = target.dataItem?.dataContext as any;
            return ctx?.[bar.colorField!] ? am5.color(ctx[bar.colorField!]) : barColor;
          });
          series.columns.template.adapters.add('stroke', (_stroke: any, target: any) => {
            const ctx = target.dataItem?.dataContext as any;
            return ctx?.[bar.colorField!] ? am5.color(ctx[bar.colorField!]) : barColor;
          });
        }

        if (bar.showValueLabel) {
          const suffix = bar.valueLabelSuffix ?? '';
          const fmt = bar.valueLabelFormatter;
          series.bullets.push(() => {
            const label = am5.Label.new(root, {
              text: `[bold]{${bar.field}}${suffix}`,
              populateText: true,
              fill: textColor,
              fontWeight: '800',
              fontSize: 10,
              fontFamily: 'Roboto',
              centerX: am5.percent(50),
              centerY: am5.percent(100),
              dy: -4,
            });
            if (fmt) {
              label.adapters.add('text', (_text, target) => {
                const value = (target.dataItem as any)?.get('valueY') as number | undefined;
                return value != null ? `[bold]${fmt(value)}` : _text;
              });
            }
            return am5.Bullet.new(root, { locationX: 0.5, locationY: 1, sprite: label });
          });
        }

        series.appear(500);
        this.barSeriesList.push(series);
      }

      // Line series (optional)
      for (const line of this.config.lines ?? []) {
        const lineColor = am5.color(line.color);
        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: line.name,
            xAxis,
            yAxis,
            categoryXField: this.config.categoryField,
            valueYField: line.field,
            stroke: lineColor,
            tooltip: am5.Tooltip.new(root, { labelText: `${line.name}: {valueY}M` }),
          })
        );
        series.strokes.template.setAll({ strokeWidth: 3 });
        series.bullets.push(() =>
          am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, { radius: 4, fill: lineColor }),
          })
        );
        series.appear(500);
        this.lineSeriesList.push(series);
      }

      // Legend (shown by default, hidden when showLegend === false)
      if (this.config.showLegend !== false) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            x: am5.percent(50),
            centerX: am5.percent(50),
            y: 0,
            centerY: 0,
          })
        );
        legend.labels.template.setAll({ fill: textColor, fontSize: 12 });
        legend.valueLabels.template.setAll({ forceHidden: true });
        legend.data.setAll(chart.series.values);
      }

      chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      chart.appear(500, 100);
    });
  }

  /** single shared dataset */
  load(data: any[]): void;
  /** per-series datasets — bars first by index, then lines; xAxis uses datasets[0] unless xAxisData is provided */
  load(datasets: any[][], xAxisData?: any[]): void;

  load(data: any[] | any[][], xAxisData?: any[]): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    const isPerSeries = data.length > 0 && Array.isArray(data[0]);
    const axisData = xAxisData ?? (isPerSeries ? (data as any[][])[0] : data as any[]) ?? [];
    this.config.data = axisData;
    this.xAxis?.data.setAll(axisData);
    if (isPerSeries) {
      const datasets = data as any[][];
      this.barSeriesList.forEach((s, i) => s.data.setAll(datasets[i] ?? axisData));
      this.lineSeriesList.forEach((s, i) => s.data.setAll(datasets[this.barSeriesList.length + i] ?? axisData));
    } else {
      this.barSeriesList.forEach(s => s.data.setAll(data as any[]));
      this.lineSeriesList.forEach(s => s.data.setAll(data as any[]));
    }
    this.isLoaded = true;
  }

  ngOnDestroy(): void {
    this.barSeriesList = [];
    this.lineSeriesList = [];
    this.root?.dispose();
  }

}
